import { useState, useEffect } from 'react';

const ADMIN_CHECK_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000/v1/admin/check'
  : '/v1/admin/check';

const CACHE_KEY = 'is_admin';

/**
 * Checks whether the currently logged-in user is an admin.
 * Result is cached in sessionStorage for the duration of the session
 * and cleared on logout (which clears sessionStorage).
 *
 * Returns { isAdmin: bool | null, loading: bool }
 * isAdmin is null while loading.
 */
export default function useIsAdmin() {
  const [isAdmin,  setIsAdmin]  = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const userId = sessionStorage.getItem('user_id') || localStorage.getItem('user_id');

    if (!userId) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    // Use cached value from this session to avoid repeated API calls
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached !== null) {
      setIsAdmin(cached === 'true');
      setLoading(false);
      return;
    }

    fetch(`${ADMIN_CHECK_URL}?user_id=${encodeURIComponent(userId)}`)
      .then(r => {
        if (!r.ok) throw new Error('check failed');
        return r.json();
      })
      .then(d => {
        const val = !!d.is_admin;
        sessionStorage.setItem(CACHE_KEY, String(val));
        setIsAdmin(val);
        setLoading(false);
      })
      .catch(() => {
        // On error assume not admin — do not cache so it retries on next mount
        setIsAdmin(false);
        setLoading(false);
      });
  }, []);

  return { isAdmin, loading };
}
